import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Badge,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BugReportIcon from '@mui/icons-material/BugReport';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BuildIcon from '@mui/icons-material/Build';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import logo from '../assets/logo0.png';
import heroImage from '../assets/hero1.png';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CloudIcon from '@mui/icons-material/Cloud';
import TranslateIcon from '@mui/icons-material/Translate';
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Styled Components
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000000',
  boxShadow: 'none',
});

const SocialIcon = styled(IconButton)({
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#007C3F',
    transform: 'translateY(-2px)',
  },
});

const ReasonCard = styled(Box)(({ theme }) => ({
  background: '#000000',
  padding: theme.spacing(4),
  height: '100%',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  }
}));

const FeatureCard = styled(Card)({
  background: 'linear-gradient(145deg, #2a2a2a 0%, #222 100%)',
  color: '#fff',
  height: '100%',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.1)',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 20px 40px rgba(0,124,63,0.2)',
    '& .MuiSvgIcon-root': {
      color: '#007C3F',
    }
  }
});

const GradientText = styled(Typography)({
  background: 'linear-gradient(45deg, #007C3F 30%, #00bf60 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontFamily: 'Roboto Condensed, sans-serif',
});

const TestimonialCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
  padding: theme.spacing(6),
  borderRadius: theme.spacing(2),
  position: 'relative',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',
}));

const SliderButton = styled(IconButton)(({ theme }) => ({
  background: 'rgba(255,255,255,0.1)',
  color: '#fff',
  '&:hover': {
    background: 'rgba(255,255,255,0.2)',
  },
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(4),
  height: '100%',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(0,124,63,0.2)',
  }
}));

const TopBar = styled(Box)(({ theme, show }) => ({
  position: 'fixed',
  width: '100%',
  top: show ? 0 : '-200px',
  left: 0,
  zIndex: 9999,
  transition: 'top 0.3s ease-in-out',
}));

const WhiteLogo = styled('img')({
  height: '30px',
  filter: 'brightness(0) invert(1)',
});

const Solutions = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();

  const features = [
    {
      icon: <BugReportIcon sx={{ fontSize: 48 }} />,
      title: "AI Fault Detection",
      description: "Advanced algorithms detect device issues with high accuracy"
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 48 }} />,
      title: "Smart Chatbot",
      description: "24/7 AI support for instant troubleshooting assistance"
    },
    {
      icon: <BuildIcon sx={{ fontSize: 48 }} />,
      title: "Spare Parts Finder",
      description: "Access genuine replacement parts from verified suppliers"
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 48 }} />,
      title: "Expert Support",
      description: "Connect with certified technicians when needed"
    }
  ];

  const testimonials = [
    {
      quote: "The For Dummies team was easy to collaborate with, care about getting the details right, and stayed on schedule. We've been excited to see how our title continues to drive leads.",
      author: "Charlie Bedell",
      company: "Illumio"
    },
    {
      quote: "Working with the For Dummies team has transformed our content strategy. Their expertise in simplifying complex topics is unmatched.",
      author: "Sarah Johnson",
      company: "TechCorp"
    },
    {
      quote: "The impact of our For Dummies custom content has exceeded our expectations. It's become our most effective lead generation tool.",
      author: "Michael Chen",
      company: "Innovation Labs"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowHeader(currentScrollY < lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handlePrevious = () => {
    setCurrentTestimonial(prev => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentTestimonial(prev => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const handleJoinClick = () => {
    navigate('/join');
  };

  return (
    <Box>
      <TopBar show={showHeader}>
        {/* Yellow Bar with Logo and Social Icons */}
        <Box sx={{ 
          bgcolor: '#FFD700',
          py: 1,
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <RouterLink to="/dashboard">
                <Box 
                  component="img"
                  src={logo}
                  alt="Larvaries"
                  sx={{ 
                    height: '30px',
                    filter: 'none',
                    cursor: 'pointer'
                  }}
                />
              </RouterLink>
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                alignItems: 'center'
              }}>
                {/* Policy and Guidelines Link */}
                <Typography 
                  variant="body2" 
                  component={RouterLink} 
                  to="/policies"
                  sx={{ 
                    color: '#000000',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Policies & Guidelines
                </Typography>
                {/* Social Icons in black */}
                <IconButton size="small" sx={{ color: '#000000' }}>
                  <FacebookIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#000000' }}>
                  <TwitterIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#000000' }}>
                  <LinkedInIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#000000' }}>
                  <InstagramIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Black Bar with Cart and Join Button */}
        <Box sx={{ 
          bgcolor: '#000000',
          py: 1.5,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }}>
                <EmojiEmotionsIcon sx={{ color: '#FFFFFF' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontWeight: 500
                  }}
                >
                  Thanks for being a customer — wishing you a great day!
                </Typography>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 3
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1 
                }}>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: '#FFFFFF',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <Badge 
                      badgeContent={3} 
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: '#FFD700',
                          color: '#000000'
                        }
                      }}
                    >
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#FFFFFF',
                      fontWeight: 500
                    }}
                  >
                    View Cart
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleJoinClick}
                  sx={{
                    bgcolor: '#FFD700',
                    color: '#000000',
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    borderRadius: 0,
                    border: 'none',
                    '&:hover': {
                      bgcolor: '#FFE44D',
                    }
                  }}
                >
                  JOIN NOW
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </TopBar>

      {/* Spacer to prevent content from hiding under fixed top bars */}
      <Box sx={{ height: '96px' }} />

      <Box sx={{ 
        bgcolor: '#0A0A0A',
        color: '#FFFFFF'
      }}>
        <StyledAppBar position="fixed">
          <Toolbar>
            <Container maxWidth="lg" sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <RouterLink to="/dashboard">
                <Box 
                  component="img" 
                  src={logo} 
                  alt="Logo" 
                  sx={{ 
                    height: 35,
                    filter: 'brightness(0) invert(1)',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }} 
                />
              </RouterLink>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SocialIcon><FacebookIcon /></SocialIcon>
                <SocialIcon><TwitterIcon /></SocialIcon>
                <SocialIcon><LinkedInIcon /></SocialIcon>
                <SocialIcon><InstagramIcon /></SocialIcon>
              </Box>
            </Container>
          </Toolbar>
        </StyledAppBar>

        <Toolbar /> {/* Spacing for fixed AppBar */}

        {/* First Section - AI Features & Pricing */}
        <Box sx={{ 
          bgcolor: '#000000', 
          py: 12,
          position: 'relative',
        }}>
          <Container maxWidth="lg">
            <Grid container spacing={6}>
              {/* AI Features */}
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    mb: 4,
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    color: '#007C3F',
                    letterSpacing: '-0.02em',
                    fontFamily: 'Inter, sans-serif',
                    textTransform: 'uppercase'
                  }}
                >
                  Intelligent Solutions
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <ChatIcon sx={{ color: '#007C3F', fontSize: 30 }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#f0f0f0', 
                        lineHeight: 1.6,
                        fontSize: '1.1rem',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500
                      }}
                    >
                      AI-powered messaging system enabling intelligent discussions and peer collaboration
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <MonetizationOnIcon sx={{ color: '#007C3F', fontSize: 30 }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#f0f0f0', 
                        lineHeight: 1.6,
                        fontSize: '1.1rem',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500
                      }}
                    >
                      Smart revenue system allowing users to earn rewards for answering questions or assisting others
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <DashboardIcon sx={{ color: '#007C3F', fontSize: 30 }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#f0f0f0', 
                        lineHeight: 1.6,
                        fontSize: '1.1rem',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500
                      }}
                    >
                      Personalized dashboards for universities, institutions, and businesses
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Pricing & Distribution */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FeatureBox>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#000000',
                          mb: 2,
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Flexible Pricing
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[
                          'Flexible subscription models for all users',
                          'Freemium model with premium AI features',
                          'Affordable digital book pricing',
                          'Special educational discounts'
                        ].map((item, index) => (
                          <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              bgcolor: '#FFD700'
                            }} />
                            <Typography sx={{ 
                              color: '#000000',
                              fontWeight: 500,
                            }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </FeatureBox>
                  </Grid>
                  <Grid item xs={12}>
                    <FeatureBox>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#000000',
                          mb: 2,
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Global Accessibility
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <CloudIcon sx={{ color: '#FFD700' }} />
                          <Typography sx={{ 
                            color: '#000000',
                            fontWeight: 500,
                          }}>
                            Cloud-based platform, accessible worldwide
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <SchoolIcon sx={{ color: '#FFD700' }} />
                          <Typography sx={{ 
                            color: '#000000',
                            fontWeight: 500,
                          }}>
                            Integration with university networks
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <TranslateIcon sx={{ color: '#FFD700' }} />
                          <Typography sx={{ 
                            color: '#000000',
                            fontWeight: 500,
                          }}>
                            AI-powered localization support
                          </Typography>
                        </Box>
                      </Box>
                    </FeatureBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Marketing Strategy Section */}
        <Box sx={{ 
          bgcolor: '#0A0A0A',
          py: 12,
          position: 'relative',
        }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                color: '#ffffff',
                mb: 6,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Three reasons to elevate your marketing strategy with our tailor-made content
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <ReasonCard>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      color: '#FFD700',
                      fontSize: '3.5rem',
                      fontWeight: 800,
                      mb: 2
                    }}
                  >
                    1
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#0066FF',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}
                  >
                    Unmatched Credibility:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#ffffff',
                      lineHeight: 1.6
                    }}
                  >
                    Leverage the trust and recognition of the Larvvaris brand to establish your authority in the industry.
                  </Typography>
                </ReasonCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <ReasonCard>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      color: '#FFD700',
                      fontSize: '3.5rem',
                      fontWeight: 800,
                      mb: 2
                    }}
                  >
                    2
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#0066FF',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}
                  >
                    Engagement:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#ffffff',
                      lineHeight: 1.6
                    }}
                  >
                    Provide your audience with a unique resource that simplifies complex ideas, making your brand more accessible and engaging.
                  </Typography>
                </ReasonCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <ReasonCard>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      color: '#FFD700',
                      fontSize: '3.5rem',
                      fontWeight: 800,
                      mb: 2
                    }}
                  >
                    3
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#0066FF',
                      mb: 2,
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}
                  >
                    Memorability:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#ffffff',
                      lineHeight: 1.6
                    }}
                  >
                    A customized Larvvaris book is a memorable way to ensure your brand stays top-of-mind with your target audience.
                  </Typography>
                </ReasonCard>
              </Grid>
            </Grid>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                sx={{ 
                  bgcolor: '#FFD700',
                  color: '#000000',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': { 
                    bgcolor: '#0066FF',
                    color: '#ffffff'
                  }
                }}
              >
                WATCH - TIPS FOR WORKING WITH LARVVARIS CUSTOM SOLUTIONS VIDEO
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Hero Section */}
        <Box sx={{ 
          bgcolor: '#0A0A0A',
          py: 12,
          position: 'relative',
        }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 6
            }}>
              {/* Text Content */}
              <Box sx={{ maxWidth: '800px' }}>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    mb: 3,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Smart AI-Powered
                  <Typography 
                    component="span"
                    variant="h1"
                    sx={{ 
                      color: '#0066FF',
                      display: 'block',
                      fontSize: 'inherit',
                      fontWeight: 'inherit'
                    }}
                  >
                    Repair Solutions
                  </Typography>
                </Typography>
                
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#FFFFFF',
                    mb: 4,
                    lineHeight: 1.6,
                    fontWeight: 500
                  }}
                >
                  Diagnose, troubleshoot, and fix your devices effortlessly with AI-driven solutions. 
                  Reduce repair costs, find genuine spare parts, and connect with experts—all in one place.
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  justifyContent: 'center',
                  mb: 6
                }}>
                  <Button 
                    variant="contained" 
                    sx={{ 
                      bgcolor: '#FFFFFF',
                      color: '#000000',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': { 
                        bgcolor: '#0066FF',
                        color: '#FFFFFF'
                      }
                    }}
                  >
                    SCAN YOUR DEVICE
                  </Button>
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      color: '#FFFFFF',
                      borderColor: '#FFFFFF',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': { 
                        borderColor: '#0066FF',
                        color: '#0066FF',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    FIND A SOLUTION
                  </Button>
                </Box>
              </Box>

              {/* Hero Image */}
              <Box 
                component="img"
                src={heroImage}
                alt="AI Repair Solutions"
                sx={{
                  width: '100%',
                  maxWidth: '1000px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              />
            </Box>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Box sx={{ 
          bgcolor: '#007C3F',
          py: 12,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.4) 0%, transparent 100%)',
          }
        }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                color: '#fff',
                mb: 8,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontFamily: 'Roboto Condensed, sans-serif'
              }}
            >
              See what our clients are saying about their custom{' '}
              <Typography 
                component="span" 
                sx={{ 
                  fontStyle: 'italic',
                  fontWeight: 700,
                  fontFamily: 'Roboto Condensed, sans-serif'
                }}
              >
                For Dummies
              </Typography>
              {' '}content
            </Typography>

            <Box sx={{ position: 'relative' }}>
              <TestimonialCard>
                <FormatQuoteIcon 
                  sx={{ 
                    fontSize: '60px', 
                    color: 'rgba(255,255,255,0.1)',
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    transform: 'rotate(180deg)'
                  }} 
                />
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#fff',
                    mb: 4,
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {testimonials[currentTestimonial].quote}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#fff',
                      fontWeight: 600,
                      mb: 1
                    }}
                  >
                    {testimonials[currentTestimonial].author}
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    {testimonials[currentTestimonial].company}
                  </Typography>
                </Box>
              </TestimonialCard>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                mt: 4
              }}>
                <SliderButton onClick={handlePrevious}>
                  <ChevronLeftIcon />
                </SliderButton>
                <SliderButton onClick={handleNext}>
                  <ChevronRightIcon />
                </SliderButton>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Features Section */}
        <Box sx={{ 
          py: 12, 
          bgcolor: '#0A0A0A',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(180deg, #000000 0%, transparent 100%)',
          }
        }}>
          <Container maxWidth="lg">
            <GradientText 
              variant="h2" 
              align="center" 
              sx={{ 
                mb: 8,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}
            >
              Powerful Features for Smart Solutions
            </GradientText>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <FeatureCard>
                    <CardContent sx={{ 
                      textAlign: 'center', 
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}>
                      <Box sx={{ color: '#fff', mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: 'Roboto Condensed, sans-serif'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#b0b0b0',
                          lineHeight: 1.6
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Solutions;